import axios from 'axios';
import { computeOverallRisk, RiskAssessment } from '../types/plan';
import { AsyncAction } from './index';

type PastDuePsm = { 'past-due': { count: number; ratio: number }; psm: { count: number; ratio: number } };

type State = {
    pastDuePsm: { fetching: boolean; stats?: PastDuePsm };
    riskProfile: { fetching: boolean; stats?: { [probability: string]: number } };
    scheduleAdherence: { fetching: boolean; stats?: { ratio: number } };
    statusCounts: { fetching: boolean; stats?: { [status: string]: number } };
    statusProgression: {
        fetching: boolean;
        interval?: Interval;
        stats?: { inspect: number; report: number; review: number };
    };
    error?: Error;
};

type Action =
    | { type: 'stats/pastDuePsm'; payload?: { stats: PastDuePsm }; error?: Error }
    | { type: 'stats/riskProfile'; payload?: { stats: { [probability: string]: number } }; error?: Error }
    | { type: 'stats/scheduleAdherence'; payload?: { stats: { ratio: number } }; error?: Error }
    | { type: 'stats/statusCounts'; payload?: { stats: { [status: string]: number } }; error?: Error }
    | {
          type: 'stats/statusProgression';
          payload?: { interval: Interval; stats: { inspect: number; report: number; review: number } };
          error?: Error;
      };

const initialState: State = {
    riskProfile: { fetching: false },
    pastDuePsm: { fetching: false },
    statusCounts: { fetching: false },
    scheduleAdherence: { fetching: false },
    statusProgression: { fetching: false },
};

export const reducer = (state = initialState, action: Action): State => {
    switch (action.type) {
        case 'stats/pastDuePsm': {
            if (action.payload) return { ...state, pastDuePsm: { fetching: false, ...action.payload } };
            if (action.error) return { ...state, pastDuePsm: { fetching: false }, error: action.error };
            return { ...state, pastDuePsm: { ...state.pastDuePsm, fetching: true } };
        }
        case 'stats/riskProfile': {
            if (action.payload) return { ...state, riskProfile: { fetching: false, ...action.payload } };
            if (action.error) return { ...state, riskProfile: { fetching: false }, error: action.error };
            return { ...state, riskProfile: { ...state.riskProfile, fetching: true } };
        }
        case 'stats/scheduleAdherence': {
            if (action.payload) return { ...state, scheduleAdherence: { fetching: false, ...action.payload } };
            if (action.error) return { ...state, scheduleAdherence: { fetching: false }, error: action.error };
            return { ...state, scheduleAdherence: { ...state.scheduleAdherence, fetching: true } };
        }
        case 'stats/statusCounts': {
            if (action.payload) return { ...state, statusCounts: { fetching: false, ...action.payload } };
            if (action.error) return { ...state, statusCounts: { fetching: false }, error: action.error };
            return { ...state, statusCounts: { ...state.statusCounts, fetching: true } };
        }
        case 'stats/statusProgression': {
            if (action.payload) return { ...state, statusProgression: { fetching: false, ...action.payload } };
            if (action.error) return { ...state, statusProgression: { fetching: false }, error: action.error };
            return { ...state, statusProgression: { ...state.statusProgression, fetching: true } };
        }
        default:
            return state;
    }
};

/**
 * Fetch past due and psm asset stats.
 *
 * @param rethrow throw the internal error
 */
const pastDuePsm = (rethrow = false): AsyncAction => async dispatch => {
    try {
        dispatch({ type: 'stats/pastDuePsm' });
        const cancel = axios.CancelToken.source();
        dispatch({ type: 'cancel/set', payload: { 'stats/pastDuePsm': cancel } });
        const response = await axios.get<{
            'past due': number;
            'past due percentage': number;
            psm: number;
            'psm percentage': number;
        }>('/inspections/dashboard/past_due', {
            cancelToken: cancel.token,
        });
        const stats: PastDuePsm = {
            'past-due': { count: response.data['past due'], ratio: response.data['past due percentage'] },
            psm: { count: response.data['psm'], ratio: response.data['psm percentage'] },
        };
        dispatch({ type: 'stats/pastDuePsm', payload: { stats } });
    } catch (error) {
        if (error.name === 'AbortError') return;
        dispatch({ type: 'stats/pastDuePsm', error });
        if (rethrow) throw error;
    }
};

/**
 * Fetch risk profile information from the backend.
 *
 * @param rethrow throw the internal error
 */
const riskProfile = (rethrow = false): AsyncAction => async dispatch => {
    try {
        dispatch({ type: 'stats/riskProfile' });
        const cancel = axios.CancelToken.source();
        dispatch({ type: 'cancel/set', payload: { 'stats/riskProfile': cancel } });
        const response = await axios.get<RiskAssessment[]>('/risk-assessments/', { cancelToken: cancel.token });
        const stats = { 'not-defined': 0, low: 0, 'moderate-low': 0, moderate: 0, 'moderate-high': 0, high: 0 };
        response.data.forEach(risk => stats[computeOverallRisk(risk)]++);
        dispatch({ type: 'stats/riskProfile', payload: { stats } });
    } catch (error) {
        if (error.name === 'AbortError') return;
        dispatch({ type: 'stats/riskProfile', error });
        if (rethrow) throw error;
    }
};

/**
 * Fetch schedule adherence metrics from the back-end.
 *
 * @param rethrow throw the internal error
 */
const scheduleAdherence = (rethrow = false): AsyncAction => async dispatch => {
    try {
        dispatch({ type: 'stats/scheduleAdherence' });
        const cancel = axios.CancelToken.source();
        dispatch({ type: 'cancel/set', payload: { 'stats/scheduleAdherence': cancel } });
        const response = await axios.get<{ adherence: number }>('/inspections/dashboard/adherence', {
            cancelToken: cancel.token,
        });
        dispatch({ type: 'stats/scheduleAdherence', payload: { stats: { ratio: response.data.adherence } } });
    } catch (error) {
        if (error.name === 'AbortError') return;
        dispatch({ type: 'stats/scheduleAdherence', error });
        if (rethrow) throw error;
    }
};

/**
 * Fetch projects status counts, including not planned and planned assets.
 *
 * @param rethrow throw the internal error
 */
const statusCounts = (rethrow = false): AsyncAction => async dispatch => {
    try {
        dispatch({ type: 'stats/statusCounts' });
        const cancel = axios.CancelToken.source();
        dispatch({ type: 'cancel/set', payload: { 'stats/statusCounts': cancel } });
        const response = await axios.get<{ [status: string]: number }>('/inspections/overview_statistics/', {
            cancelToken: cancel.token,
        });
        dispatch({ type: 'stats/statusCounts', payload: { stats: response.data } });
    } catch (error) {
        if (error.name === 'AbortError') return;
        dispatch({ type: 'stats/statusCounts', error });
        if (rethrow) throw error;
    }
};

/**
 * Fetch project status average times to progression.
 *
 * @param rethrow throw the internal error
 */
const statusProgression = (interval: Interval, rethrow = false): AsyncAction => async dispatch => {
    try {
        dispatch({ type: 'stats/statusProgression' });
        const cancel = axios.CancelToken.source();
        dispatch({ type: 'cancel/set', payload: { 'stats/statusProgression': cancel } });
        const response = await axios.get<{
            average_to_inspect: number;
            average_to_report: number;
            average_to_review: number;
        }>('/inspections/dashboard/metrics/', {
            params: { start_date: new Date(interval.start), end_date: new Date(interval.end) },
            cancelToken: cancel.token,
        });
        const stats = {
            inspect: response.data.average_to_inspect,
            report: response.data.average_to_report,
            review: response.data.average_to_review,
        };
        dispatch({ type: 'stats/statusProgression', payload: { interval, stats } });
    } catch (error) {
        if (error.name === 'AbortError') return;
        dispatch({ type: 'stats/statusProgression', error });
        if (rethrow) throw error;
    }
};

export const creators = { riskProfile, pastDuePsm, statusCounts, scheduleAdherence, statusProgression };
